import { useEffect, useMemo, useState } from "react";
import { phase1Api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import PortalSidebar from "../components/PortalSidebar";
import PortalTopbar from "../components/PortalTopbar";

function Modal({ title, eyebrow, children, onClose }) {
  return (
    <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
      <section className="tazy-modal-card order-flow-modal">
        <header className="tazy-modal-header">
          <div>
            <span>{eyebrow}</span>
            <h3>{title}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close modal">×</button>
        </header>
        <div className="tazy-modal-body">{children}</div>
      </section>
    </div>
  );
}

function statusTone(value) {
  const status = String(value || "").toUpperCase();
  if (["RESPONDED", "CONVERTED", "CONFIRMED", "COMPLETED"].includes(status)) return "good";
  if (["NEW", "SENT_TO_SELLER", "PENDING_SELLER"].includes(status)) return "warn";
  if (["CLOSED", "CANCELLED", "REJECTED"].includes(status)) return "bad";
  return "neutral";
}

function formatError(err) {
  const data = err?.response?.data;
  if (!data) return "Action failed. Please try again.";
  if (data.detail) return data.detail;
  const key = Object.keys(data)[0];
  if (!key) return "Action failed.";
  return Array.isArray(data[key]) ? `${key}: ${data[key].join(", ")}` : `${key}: ${data[key]}`;
}

export default function EnquiriesWorkspace() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [selected, setSelected] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [busy, setBusy] = useState(false);

  const isSellerSide = ["SELLER", "BUSINESS"].includes(user?.account_type) || user?.is_platform_staff;
  const isBuyerSide = user?.account_type === "BUYER" || user?.account_type === "BUSINESS";

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await phase1Api.enquiries();
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError("Unable to load enquiries right now.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => ({
    total: rows.length,
    newItems: rows.filter((row) => ["NEW", "SENT_TO_SELLER"].includes(row.status)).length,
    responded: rows.filter((row) => row.status === "RESPONDED").length,
    converted: rows.filter((row) => row.status === "CONVERTED").length,
  }), [rows]);

  function openRespond(row) {
    setSelected(row);
    setResponseText(row.seller_response || "");
    setMessage("");
    setError("");
  }

  async function respond(action) {
    if (!selected?.id) return;
    setBusy(true);
    setMessage("");
    setError("");
    try {
      await phase1Api.enquiryDecision(selected.id, { action, notes: responseText });
      setSelected(null);
      setResponseText("");
      setMessage(action === "CLOSE" ? "Enquiry closed." : "Seller response sent. Buyer can now place an order from this enquiry.");
      await load();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  }

  async function placeOrder(row) {
    setBusy(true);
    setMessage("");
    setError("");
    try {
      const res = await phase1Api.createOrderFromEnquiry(row.id, {});
      setMessage(`Order ${res.data.order_number || ""} created. You can now track it under Orders.`);
      await load();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`portal-shell-v2 refined-portal-shell ${collapsed ? "is-collapsed" : ""}`}>
      <PortalSidebar collapsed={collapsed} onToggle={() => setCollapsed((value) => !value)} />
      <main className="portal-main-v2 records-main-clean enquiry-workspace-main">
        <PortalTopbar user={user} onLogout={logout} />

        <section className="orders-hero-v2 enquiry-hero-v2">
          <div>
            <span>TRADE FLOW</span>
            <h1>{isSellerSide ? "Buyer enquiries" : "My purchase enquiries"}</h1>
            <p>{isSellerSide ? "Respond to buyer interest, confirm availability, pricing and delivery terms, then allow the buyer to place an order." : "Track seller responses and place orders when you are ready to proceed."}</p>
          </div>
          <div className="order-flow-kpis">
            <div><strong>{stats.total}</strong><span>Total</span></div>
            <div><strong>{stats.newItems}</strong><span>Pending</span></div>
            <div><strong>{stats.responded}</strong><span>Responded</span></div>
            <div><strong>{stats.converted}</strong><span>Ordered</span></div>
          </div>
        </section>

        {message && <div className="success-box workspace-alert">{message}</div>}
        {error && <div className="error-box workspace-alert">{error}</div>}

        <section className="records-card phase1b-records-card order-flow-card">
          <div className="records-card__header">
            <div><span>LIVE ENQUIRIES</span><h3>Buyer-seller communication queue</h3></div>
            <button type="button" onClick={load}>Refresh</button>
          </div>

          {loading ? <div className="records-empty">Loading enquiries...</div> : rows.length === 0 ? <div className="records-empty">No enquiries yet.</div> : (
            <div className="enquiry-list-v2">
              {rows.map((row) => (
                <article className="enquiry-card-v2" key={row.id}>
                  <div className="enquiry-card-main">
                    <div className="enquiry-card-title-row">
                      <span className={`status-chip ${statusTone(row.status)}`}>{row.status}</span>
                      <strong>{row.code}</strong>
                    </div>
                    <h3>{row.product_title || "Marketplace product"}</h3>
                    <p>{row.message || "No buyer message provided."}</p>
                    {row.seller_response && <div className="seller-response-box"><span>Seller response</span><p>{row.seller_response}</p></div>}
                  </div>
                  <div className="enquiry-card-side">
                    <dl>
                      <div><dt>Buyer</dt><dd>{row.buyer_name || "—"}</dd></div>
                      <div><dt>Seller</dt><dd>{row.seller_name || "—"}</dd></div>
                      <div><dt>Quantity</dt><dd>{row.quantity}</dd></div>
                      <div><dt>Delivery area</dt><dd>{row.preferred_delivery_area || "—"}</dd></div>
                    </dl>
                    <div className="enquiry-actions-v2">
                      {isSellerSide && row.status !== "CONVERTED" && row.status !== "CLOSED" && <button type="button" onClick={() => openRespond(row)}>Respond</button>}
                      {isBuyerSide && row.status === "RESPONDED" && <button type="button" className="primary-action-btn" disabled={busy} onClick={() => placeOrder(row)}>Place order</button>}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      {selected && (
        <Modal title="Respond to buyer enquiry" eyebrow="SELLER ACTION" onClose={() => setSelected(null)}>
          <div className="enquiry-response-summary">
            <strong>{selected.product_title}</strong>
            <p>Buyer requested {selected.quantity} unit(s), delivery area: {selected.preferred_delivery_area || "not specified"}.</p>
          </div>
          <label className="modal-field-full">Response to buyer
            <textarea value={responseText} onChange={(e) => setResponseText(e.target.value)} rows="5" placeholder="Confirm availability, final terms, delivery expectations or any required payment steps." />
          </label>
          <div className="modal-action-row">
            <button type="button" disabled={busy} onClick={() => respond("RESPOND")}>Send response</button>
            <button type="button" disabled={busy} onClick={() => respond("ORDER_READY")}>Mark ready for order</button>
            <button type="button" disabled={busy} className="danger-soft" onClick={() => respond("CLOSE")}>Close enquiry</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
